import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useState } from 'react';

import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import {
  formatarEnquantoDigita,
  PAIS_PADRAO,
  PAISES,
  paisDoE164,
  paraE164,
  parteNacional,
} from '@/shared/lib/telefone';
import { ComboBox, type OpcaoCombo } from '@/shared/ui/combobox';
import { Input } from '@/shared/ui/input';

/**
 * Opções montadas UMA vez, fora do componente: são ~240 itens e a lista é
 * constante. Remontá-las a cada tecla digitada no telefone invalidaria o
 * `useMemo` do filtro do ComboBox a cada render.
 */
const OPCOES: OpcaoCombo[] = PAISES.map((pais) => ({
  valor: pais.codigo,
  rotulo: `${pais.bandeira} ${pais.ddi}`,
  // O nome vai no `detalhe`, não no rótulo: o gatilho é estreito e mostraria
  // "🇧🇷 +55 Brasil" truncado. Na busca, `detalhe` é procurado junto — quem
  // digita "Portugal" acha, quem digita "351" também.
  detalhe: pais.nome,
}));

interface Props {
  id: string;
  /** E.164 (`+5511999990000`) ou `''`. */
  valor: string;
  aoMudar: (e164: string) => void;
  aoSair?: () => void;
  desabilitado?: boolean;
  'aria-label'?: string;
}

/**
 * Telefone com bandeira do país e a máscara respectiva.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  **O valor que entra e sai é sempre E.164** (`+5511999990000`). A máscara
 *  ((11) 99999-0000) é estado interno da tela e nunca chega ao formulário — é
 *  o que permite o mesmo número servir para o link do WhatsApp sem adivinhação.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ Emite E.164 **parcial** enquanto se digita, de propósito. Se emitisse `''`
 * para número incompleto, um telefone digitado pela metade seria salvo como
 * "não informado", em silêncio. Emitindo o parcial, o zod de quem consome
 * recusa com mensagem — ver `paraE164`.
 *
 * O país é um `ComboBox` e não um `<Select>` do Radix: são 240 itens, e sem
 * busca achar "Portugal" é rolagem cega.
 */
export function CampoDeTelefone({
  id,
  valor,
  aoMudar,
  aoSair,
  desabilitado = false,
  'aria-label': ariaLabel = 'Telefone',
}: Props) {
  const [pais, setPais] = useState<CountryCode>(() => paisDoE164(valor) ?? PAIS_PADRAO);
  const [texto, setTexto] = useState(() => parteNacional(valor));

  /**
   * Ressincroniza quando o valor muda POR FORA (o `reset()` do formulário ao
   * abrir o diálogo com outro tutor).
   *
   * A comparação com `paraE164(texto, pais)` é o que impede o efeito de
   * reescrever o que está sendo digitado: durante a digitação os dois já são o
   * mesmo número, e o efeito não faz nada.
   */
  useEffect(() => {
    if (valor === paraE164(texto, pais)) return;

    setPais(paisDoE164(valor) ?? PAIS_PADRAO);
    setTexto(parteNacional(valor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);

  function aoDigitar(digitado: string) {
    // Formata primeiro e guarda o FORMATADO: guardar o cru e formatar só na
    // renderização faz o cursor pular para o fim a cada tecla.
    const formatado = formatarEnquantoDigita(digitado, pais);
    setTexto(formatado);
    aoMudar(paraE164(formatado, pais));
  }

  function aoTrocarPais(codigo: string) {
    const novo = codigo as CountryCode;
    setPais(novo);

    // Reformata o que já estava digitado para a máscara do país novo — quem
    // troca de país depois de digitar não deveria ter de apagar tudo.
    const reformatado = formatarEnquantoDigita(texto.replace(/\D/g, ''), novo);
    setTexto(reformatado);
    aoMudar(paraE164(reformatado, novo));
  }

  return (
    <div className="flex gap-2">
      <ComboBox
        valor={pais}
        aoEscolher={aoTrocarPais}
        opcoes={OPCOES}
        desabilitado={desabilitado}
        placeholderBusca="País ou código..."
        aria-label="País do telefone"
        className={`w-[7.5rem] shrink-0 ${CLASSE_CONTROLE}`}
      />

      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={texto}
        onChange={(evento) => aoDigitar(evento.target.value)}
        onBlur={aoSair}
        disabled={desabilitado}
        aria-label={ariaLabel}
        className={CLASSE_CONTROLE}
      />
    </div>
  );
}
